import React from 'react';

export interface BentoGridBlockProps {
    topLeftImage: string;
    topLeftTitle?: string;
    topLeftSubtitle?: string;
    topLeftLink?: string;
    topLeftLinkText?: string;

    topRightImage: string;
    topRightTitle?: string;
    topRightSubtitle?: string;
    topRightLink?: string;
    topRightLinkText?: string;

    bottomLeftImage: string;
    bottomLeftTitle?: string;
    bottomLeftSubtitle?: string;
    bottomLeftLink?: string;
    bottomLeftLinkText?: string;

    bottomCenterImage: string;
    bottomCenterTitle?: string;
    bottomCenterSubtitle?: string;
    bottomCenterLink?: string;
    bottomCenterLinkText?: string;

    bottomRightImage: string;
    bottomRightTitle?: string;
    bottomRightSubtitle?: string;
    bottomRightLink?: string;
    bottomRightLinkText?: string;

    style?: {
        backgroundColor?: string;
        cardBackgroundColor?: string;
        textColor?: string;
        paddingTop?: string;
        paddingBottom?: string;
        // Global fallback
        imageObjectFit?: any;
        imageObjectPosition?: string;
        // Specific
        topLeftFit?: any;
        topLeftPosition?: string;
        topRightFit?: any;
        topRightPosition?: string;
        bottomLeftFit?: any;
        bottomLeftPosition?: string;
        bottomCenterFit?: any;
        bottomCenterPosition?: string;
        bottomRightFit?: any;
        bottomRightPosition?: string;
    };
}

export default function BentoGridBlock({
    topLeftImage,
    topLeftTitle,
    topLeftSubtitle,
    topLeftLink,
    topLeftLinkText,
    topRightImage,
    topRightTitle,
    topRightSubtitle,
    topRightLink,
    topRightLinkText,
    bottomLeftImage,
    bottomLeftTitle,
    bottomLeftSubtitle,
    bottomLeftLink,
    bottomLeftLinkText,
    bottomCenterImage,
    bottomCenterTitle,
    bottomCenterSubtitle,
    bottomCenterLink,
    bottomCenterLinkText,
    bottomRightImage,
    bottomRightTitle,
    bottomRightSubtitle,
    bottomRightLink,
    bottomRightLinkText,
    style = {}
}: BentoGridBlockProps) {
    const cardBg = style.cardBackgroundColor || 'bg-gray-800';
    const isHexCardBg = cardBg.startsWith('#');

    const renderContent = (title?: string, subtitle?: string, link?: string, linkText?: string, align: 'left' | 'center' = 'left') => {
        if (!title && !subtitle && !linkText) return null;
        return (
            <div className={`absolute inset-0 flex flex-col justify-end ${align === 'center' ? 'items-center text-center' : 'items-start text-left'} p-8 z-20`}>
                {subtitle && (
                    <span className="text-[10px] md:text-xs tracking-[0.2em] uppercase mb-4 opacity-80">
                        {subtitle}
                    </span>
                )}
                {title && (
                    <h3 className="text-xl md:text-2xl font-light leading-tight max-w-md font-serif mb-6">
                        {title}
                    </h3>
                )}
                {link && linkText && (
                    <a href={link} className="inline-block px-6 py-2 bg-white text-black text-sm font-medium rounded-full hover:bg-gray-200 transition-colors">
                        {linkText}
                    </a>
                )}
            </div>
        );
    };

    const renderCard = (
        image: string,
        title?: string,
        subtitle?: string,
        link?: string,
        linkText?: string,
        fit?: any,
        position?: string,
        heightClass: string = "h-[300px] md:h-full"
    ) => (
        <div
            className={`relative w-full ${heightClass} rounded-2xl overflow-hidden ${!isHexCardBg ? cardBg : ''}`}
            style={isHexCardBg ? { backgroundColor: cardBg } : {}}
        >
            {image && (
                <div className="absolute inset-0">
                    <img
                        src={image}
                        alt={title || "Bento grid item"}
                        className="w-full h-full"
                        style={{
                            objectFit: fit || style.imageObjectFit || 'cover',
                            objectPosition: position || style.imageObjectPosition || 'center',
                        }}
                    />
                    <div className="absolute inset-0 bg-black/20" />
                </div>
            )}
            {renderContent(title, subtitle, link, linkText)}
        </div>
    );

    return (
        <div
            className="w-full px-4 md:px-12 lg:px-24 py-8"
            style={{
                backgroundColor: style.backgroundColor || 'transparent',
                paddingTop: style.paddingTop || '2rem',
                paddingBottom: style.paddingBottom || '2rem',
                color: style.textColor || '#FFFFFF',
            }}
        >
            {/* Context: 2 Rows Layout */}

            {/* Row 1: 2 Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 h-auto md:h-[500px] mb-8">
                {/* Top Left */}
                {renderCard(topLeftImage, topLeftTitle, topLeftSubtitle, topLeftLink, topLeftLinkText, style.topLeftFit, style.topLeftPosition)}

                {/* Top Right */}
                {renderCard(topRightImage, topRightTitle, topRightSubtitle, topRightLink, topRightLinkText, style.topRightFit, style.topRightPosition)}
            </div>

            {/* Row 2: 3 Columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 h-auto md:h-[400px]">
                {/* Bottom Left */}
                {renderCard(bottomLeftImage, bottomLeftTitle, bottomLeftSubtitle, bottomLeftLink, bottomLeftLinkText, style.bottomLeftFit, style.bottomLeftPosition)}

                {/* Bottom Center */}
                {renderCard(bottomCenterImage, bottomCenterTitle, bottomCenterSubtitle, bottomCenterLink, bottomCenterLinkText, style.bottomCenterFit, style.bottomCenterPosition)}

                {/* Bottom Right */}
                {renderCard(bottomRightImage, bottomRightTitle, bottomRightSubtitle, bottomRightLink, bottomRightLinkText, style.bottomRightFit, style.bottomRightPosition)}
            </div>
        </div>
    );
}
